<template>
  <div class="vanilla-a-wrap">
    <main class="screen" role="main" aria-labelledby="question-heading">
      <div class="header">
        <button
          type="button"
          class="back-button"
          @click="goBack"
          :disabled="currentIndex === 0"
          aria-label="Zurück"
        >←</button>
        <span class="question-counter">
          Frage {{ currentIndex + 1 }} von {{ questions.length }}
        </span>
      </div>

      <div
        class="progress-bar"
        role="progressbar"
        :aria-valuenow="currentIndex + 1"
        aria-valuemin="1"
        :aria-valuemax="questions.length"
        aria-label="Fortschritt"
      >
        <div
          v-for="seg in progressSegments"
          :key="seg.sectionId"
          class="progress-segment"
          :style="{ flex: seg.size }"
        >
          <div
            v-if="seg.fill > 0"
            class="progress-segment-fill"
            :style="{ width: (seg.fill * 100) + '%' }"
          ></div>
        </div>
      </div>

      <div class="question-block">
        <p class="section-label">
          {{ currentSection.label }} · Frage {{ withinSectionIndex + 1 }} von {{ currentSection.count }}
        </p>
        <h1 id="question-heading" class="question-title">{{ currentQuestion.title }}</h1>
        <p class="question-prompt">{{ currentQuestion.prompt }}</p>
      </div>

      <button
        v-if="currentQuestion.tooltip"
        type="button"
        class="tooltip-button"
        :aria-expanded="String(tooltipOpen)"
        :aria-controls="tooltipId"
        @click="tooltipOpen = !tooltipOpen"
      >
        <span class="tooltip-icon" aria-hidden="true">i</span>
        Was zählt hier?
      </button>

      <div
        v-if="currentQuestion.tooltip && tooltipOpen"
        :id="tooltipId"
        class="tooltip-content"
      >
        {{ currentQuestion.tooltip }}
      </div>

      <fieldset class="answer-fieldset" :key="currentQuestion.id">
        <legend class="sr-only">Antwortoptionen</legend>
        <label
          v-for="opt in currentQuestion.options"
          :key="opt.value"
          class="answer-option"
        >
          <input
            type="radio"
            :name="`q-${currentQuestion.id}`"
            :value="opt.value"
            v-model="answers[currentQuestion.id]"
          >
          <span class="answer-option-label">{{ opt.label }}</span>
        </label>
      </fieldset>

      <div class="footer">
        <button
          type="button"
          class="next-button"
          :disabled="!hasAnswer"
          @click="goNext"
        >
          {{ isLast ? 'Fertig' : 'Weiter' }}
        </button>
      </div>
    </main>
  </div>
</template>

<script>
import { sections, questions } from '../_data/sampleQuestions.js';

export default {
  name: 'VanillaA',
  data() {
    return {
      questions,
      currentIndex: 0,
      tooltipOpen: false,
      answers: {},
    };
  },
  computed: {
    sectionsWithCounts() {
      return sections.map(s => ({
        ...s,
        count: this.questions.filter(q => q.sectionId === s.id).length,
        firstIdx: this.questions.findIndex(q => q.sectionId === s.id),
        lastIdx: (() => {
          let last = -1;
          this.questions.forEach((q, i) => { if (q.sectionId === s.id) last = i; });
          return last;
        })(),
      })).filter(s => s.count > 0);
    },
    currentQuestion() {
      return this.questions[this.currentIndex];
    },
    currentSection() {
      return this.sectionsWithCounts.find(s => s.id === this.currentQuestion.sectionId);
    },
    withinSectionIndex() {
      return this.currentIndex - this.currentSection.firstIdx;
    },
    progressSegments() {
      return this.sectionsWithCounts.map(s => {
        let fill = 0;
        if (this.currentIndex > s.lastIdx) {
          fill = 1;
        } else if (this.currentIndex >= s.firstIdx) {
          fill = (this.currentIndex - s.firstIdx + 1) / s.count;
        }
        return { sectionId: s.id, size: s.count, fill };
      });
    },
    hasAnswer() {
      return this.answers[this.currentQuestion.id] !== undefined;
    },
    isLast() {
      return this.currentIndex === this.questions.length - 1;
    },
    tooltipId() {
      return `tooltip-q${this.currentQuestion.id}`;
    },
  },
  methods: {
    goBack() {
      if (this.currentIndex === 0) return;
      this.currentIndex--;
      this.tooltipOpen = false;
    },
    goNext() {
      if (!this.hasAnswer) return;
      if (this.isLast) {
        this.$router.push('/postsurvey/email');
        return;
      }
      this.currentIndex++;
      this.tooltipOpen = false;
    },
  },
};
</script>

<style scoped>
.vanilla-a-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-progress-fill: #534AB7;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.vanilla-a-wrap *,
.vanilla-a-wrap *::before,
.vanilla-a-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1rem 1.25rem;
  min-height: 600px;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.back-button {
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 20px;
  color: var(--color-text-secondary);
  line-height: 1;
}

.back-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.question-counter {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.progress-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 1.5rem;
}

.progress-segment {
  height: 6px;
  border-radius: 3px;
  background: var(--color-bg-muted);
  overflow: hidden;
}

.progress-segment-fill {
  height: 100%;
  background: var(--color-progress-fill);
  border-radius: 3px;
  transition: width 0.25s ease;
}

.question-block {
  margin-bottom: 1.25rem;
}

.section-label {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 6px;
}

.question-title {
  font-size: 22px;
  font-weight: 500;
  margin: 0 0 8px;
  line-height: 1.3;
}

.question-prompt {
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0;
}

.tooltip-button {
  background: transparent;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 8px 12px;
  font-size: 13px;
  color: var(--color-text-secondary);
  cursor: pointer;
  align-self: flex-start;
  margin-bottom: 1.5rem;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.tooltip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--color-progress-fill);
  color: white;
  font-size: 11px;
  font-weight: 500;
  font-style: italic;
  font-family: Georgia, serif;
  line-height: 1;
}

.tooltip-button:hover {
  background: var(--color-bg-muted);
}

.tooltip-content {
  background: var(--color-bg-muted);
  border-radius: var(--radius-md);
  padding: 12px 14px;
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.answer-fieldset {
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.answer-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  background: var(--color-bg-card);
  transition: background 0.15s, border-color 0.15s;
}

.answer-option:hover {
  background: var(--color-bg-muted);
}

.answer-option:has(input:checked) {
  border-color: var(--color-progress-fill);
  background: var(--color-bg-muted);
}

.answer-option input[type="radio"] {
  margin: 0;
  accent-color: var(--color-progress-fill);
}

.answer-option-label {
  font-size: 15px;
}

.footer {
  margin-top: auto;
  padding-top: 1.5rem;
}

.next-button {
  width: 100%;
  padding: 14px;
  background: var(--color-bg-muted);
  color: var(--color-text-tertiary);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 15px;
  font-family: inherit;
  cursor: not-allowed;
  transition: background 0.15s;
}

.next-button:not(:disabled) {
  background: var(--color-progress-fill);
  color: white;
  border-color: var(--color-progress-fill);
  cursor: pointer;
}

.next-button:not(:disabled):hover {
  opacity: 0.9;
}
</style>
